/**
 * Друкована форма «Рахунок на оплату» (Порція А продажів, 2026-07-24).
 *
 * ч.1373 (рішення власника): верстка — за зразком 1С («Рахунок 68 від 26.10.2026.xlsx» у
 * import-sources): попередження над рахунком, «Зразок заповнення платіжного доручення»,
 * постачальник / покупець з реквізитами, таблиця з сірою шапкою, підсумки, сума прописом,
 * «Виписав(ла)». Дані — одна модель (`invoicePrintModel`), з неї ж «зберегти в Excel»
 * складає книгу на сітці 1С (`POST /print/xlsx/invoice`).
 *
 * Роут: /customer-invoices/:id/print (кнопка «друк» у редакторі рахунку).
 */
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { customerOrders } from '@/api/orders';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { printExport } from '@/api/printExport';
import PrintActions from '@/shared/components/PrintActions';
import { vatHeaders } from '@/shared/components/PrintLines1c';
import {
  buildInvoiceModel, fmtMoney, fmtQty, invoiceXlsxPayload, type InvoiceDoc,
} from '@/shared/utils/invoicePrintModel';

export default function CustomerInvoicePrintPage() {
  const { id } = useParams<{ id: string }>();

  // Друк за ЄДИНОЮ механікою друкованих форм (body.print-mode +
  // .print-page): у друк йде ЛИШЕ бланк, без layout застосунку.
  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const docQ = useQuery({
    queryKey: ['customer-order', id],
    queryFn: () => customerOrders.get(id!),
    enabled: !!id,
  });
  const d = docQ.data;

  const orgQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const org = orgQ.data?.items.find((o) => o.id === d?.organization_id);
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const contractor = contractorsQ.data?.items.find((c) => c.id === d?.contractor_id);
  const contractsQ = useQuery({
    queryKey: ['contracts', d?.organization_id, d?.contractor_id],
    queryFn: () => refs.contracts({
      organization_id: d!.organization_id, contractor_id: d!.contractor_id,
    }),
    enabled: !!d,
  });
  const contract = contractsQ.data?.find((c) => c.id === d?.contract_id);
  const bankAccountsQ = useQuery({
    queryKey: ['bank-accounts', d?.organization_id],
    queryFn: () => refs.bankAccounts(d!.organization_id),
    enabled: !!d,
  });
  // Рахунок для оплати: вказаний у документі > основний > перший.
  const bankAccount =
    bankAccountsQ.data?.find((b) => b.id === (d as { bank_account_id?: string | null } | undefined)?.bank_account_id)
    ?? bankAccountsQ.data?.find((b) => b.is_main)
    ?? bankAccountsQ.data?.[0];
  // Рахунок покупця — як у 1С, під його назвою: основний > перший.
  const ctrBankQ = useQuery({
    queryKey: ['contractor-bank-accounts', d?.contractor_id],
    queryFn: () => refs.contractorBankAccounts(d!.contractor_id),
    enabled: !!d?.contractor_id,
  });
  const ctrBank = ctrBankQ.data?.find((b) => b.is_main) ?? ctrBankQ.data?.[0];
  const banksQ = useQuery({
    queryKey: ['banks'],
    queryFn: () => refs.banks(),
    staleTime: 1000 * 60 * 60,
  });
  const nomListQ = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.listAll(),
  });
  const uomsQ = useQuery({ queryKey: ['uoms'], queryFn: refs.uoms });
  // 2026-07-26: рахунок «на підставі замовлення» — № замовлення у друк.
  const baseOrderId = (d as { base_order_id?: string | null } | undefined)?.base_order_id;
  const baseOrderQ = useQuery({
    queryKey: ['customer-order', baseOrderId],
    queryFn: () => customerOrders.get(baseOrderId!),
    enabled: !!baseOrderId,
  });

  if (!d || !org) {
    return <div style={{ padding: '2rem' }}>Завантаження рахунку…</div>;
  }

  // Представлення договору при друку — з картки договору:
  // print_deal_info=false → рядок договору не друкуємо;
  // print_name → замість стандартного «name №number».
  const contractLine = contract && contract.print_deal_info !== false
    ? (contract.print_name
      || `${contract.name}${contract.number ? ` №${contract.number}` : ''}`)
    : null;

  const m = buildInvoiceModel({
    doc: d as unknown as InvoiceDoc,
    org,
    contractor: contractor ?? null,
    contractLine,
    orgIban: bankAccount?.iban ?? null,
    contractorIban: ctrBank?.iban ?? null,
    banks: banksQ.data ?? [],
    nomenclature: new Map((nomListQ.data?.items ?? []).map((n) => [n.id, n])),
    uoms: new Map((uomsQ.data ?? []).map((u) => [u.id, u.short_name])),
    baseOrder: baseOrderQ.data ?? null,
  });
  const filename = `Рахунок ${d.document_number}`;

  return (
    <div className="print-page inv1c">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: A4; margin: 1cm 1.2cm; }
        }
        .inv1c {
          background: white; color: black; max-width: 760px; margin: 0 auto; padding: 1.5rem;
          font-family: Arial, Helvetica, sans-serif; font-size: 9pt; line-height: 1.25;
        }
        .inv1c .notice {
          width: 84%; margin: 0 auto 0.5rem; border: 1px solid #000; padding: 2px 6px;
          font-size: 7.5pt; text-align: center;
        }
        .inv1c .pay { width: 100%; border: 1px solid #000; border-collapse: separate; border-spacing: 0;
          table-layout: fixed; font-family: 'Times New Roman', serif; font-size: 9pt; }
        .inv1c .pay td { padding: 1px 3px; vertical-align: middle; }
        .inv1c .pay .ttl { font-family: Arial, Helvetica, sans-serif; font-weight: bold;
          text-align: center; padding-bottom: 10px; }
        .inv1c .pay .bx { border: 2px solid #000; text-align: center; font-weight: bold; height: 1.6em; }
        .inv1c .pay .ul { border-bottom: 1px solid #000; font-weight: bold; }
        .inv1c .pay .c { text-align: center; }
        .inv1c h1 { font-family: Arial, Helvetica, sans-serif; font-size: 14pt; font-weight: bold; color: #000;
          margin: 1.1rem 0 0.9rem;
          padding-bottom: 3px; border-bottom: 2px solid #000; }
        .inv1c .party { display: grid; grid-template-columns: 13% 1fr; column-gap: 6px; margin-bottom: 0.45rem; }
        .inv1c .party .lbl { text-decoration: underline; }
        .inv1c .party .name { font-weight: bold; }
        .inv1c .party .det { grid-column: 2; padding-left: 2%; }
        .inv1c .req { display: grid; grid-template-columns: 26% 1fr; }
        .inv1c table.items { width: 100%; border-collapse: collapse; margin: 0.4rem 0 0.3rem;
          border: 2px solid #000; font-size: 8pt; }
        .inv1c table.items th { background: #eee; font-size: 9pt; font-weight: bold; text-align: center;
          border: 1px solid #000; padding: 3px 2px; text-transform: none; letter-spacing: 0; color: #000; }
        .inv1c table.items td { border: 1px solid #000; padding: 0 3px; vertical-align: top; }
        .inv1c table.items .r { text-align: right; white-space: nowrap; }
        .inv1c table.items .c { text-align: center; }
        .inv1c table.items td.qty { border-right: none; }
        .inv1c table.items td.uom { border-left: none; }
        .inv1c .totals { width: 100%; border-collapse: collapse; font-weight: bold; }
        .inv1c .totals td { padding: 1px 3px; }
        .inv1c .words { font-weight: bold; margin: 0.15rem 0 0.4rem; padding-bottom: 0.4rem;
          border-bottom: 2px solid #000; }
        .inv1c .signer { display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 1.2rem;
          font-weight: bold; }
        .inv1c .signer .line { width: 36%; border-bottom: 1px solid #000; text-align: right;
          font-weight: normal; }
      `}</style>

      <PrintActions
        docMeta={{
          organizationId: d.organization_id,
          documentType: 'doc.customer_orders',
          documentId: d.id,
          filename,
          orgName: org.name,
          contractorEmail: contractor?.email ?? null,
        }}
        xlsxBuild={() => printExport.invoiceXlsx(invoiceXlsxPayload(m, filename))}
      />

      {m.notice && <div className="notice">{m.notice}</div>}

      <table className="pay">
        <colgroup>
          <col style={{ width: '10%' }} /><col style={{ width: '18%' }} /><col style={{ width: '14%' }} />
          <col style={{ width: '3%' }} /><col style={{ width: '11%' }} /><col style={{ width: '2%' }} />
          <col style={{ width: '25%' }} /><col style={{ width: '17%' }} />
        </colgroup>
        <tbody>
          <tr><td colSpan={8} className="ttl">Зразок заповнення платіжного доручення</td></tr>
          <tr>
            <td>Одержувач</td>
            <td colSpan={4}><b>{m.recipient.name}</b></td>
            <td colSpan={3} />
          </tr>
          <tr>
            <td>Код</td>
            <td className="bx">{m.recipient.code}</td>
            <td colSpan={4} />
            <td className="c">КРЕДИТ рах. N</td>
            <td />
          </tr>
          <tr>
            <td colSpan={3}>Банк одержувача</td>
            <td />
            <td className="c">Код банку</td>
            <td />
            <td className="bx" style={{ fontSize: '8pt' }}>{m.recipient.iban}</td>
            <td />
          </tr>
          <tr>
            <td colSpan={3} className="ul">{m.recipient.bank}</td>
            <td />
            <td className="bx">{m.recipient.mfo}</td>
            <td />
            <td className="bx" />
            <td />
          </tr>
          <tr><td colSpan={8} style={{ height: 6 }} /></tr>
        </tbody>
      </table>

      <h1>{m.title}</h1>

      <div className="party">
        <span className="lbl">Постачальник:</span>
        <span className="name">{m.supplierName}</span>
        {m.supplierDetails.length > 0 && (
          <div className="det">{m.supplierDetails.map((l) => <div key={l}>{l}</div>)}</div>
        )}
      </div>
      <div className="party">
        <span className="lbl">Покупець:</span>
        <span className="name">{m.buyerName}</span>
        {m.buyerDetails.length > 0 && (
          <div className="det">{m.buyerDetails.map((l) => <div key={l}>{l}</div>)}</div>
        )}
      </div>
      <div className="party" style={{ fontSize: '10pt' }}>
        <span>Договір:</span>
        <span>{m.contract}</span>
      </div>
      {m.requisites.map(([label, value]) => (
        <div className="req" key={label}><span>{label}:</span><span>{value}</span></div>
      ))}

      <table className="items">
        <thead>
          <tr>
            <th style={{ width: '5%' }}>№</th>
            <th>Товари (роботи, послуги)</th>
            {m.hasDiscount && <th style={{ width: '8%' }}>Знижка, %</th>}
            <th colSpan={2} style={{ width: '14%' }}>Кількість</th>
            <th style={{ width: '11%' }}>{vatHeaders(m.mode).price}</th>
            <th style={{ width: '12%' }}>{vatHeaders(m.mode).amount}</th>
          </tr>
        </thead>
        <tbody>
          {m.items.map((it, i) => (
            <tr key={i}>
              <td className="c">{i + 1}</td>
              <td>{it.name}</td>
              {m.hasDiscount && (
                <td className="r">
                  {it.discount != null ? it.discount.toLocaleString('uk-UA', { maximumFractionDigits: 2 }) : ''}
                </td>
              )}
              <td className="r qty">{fmtQty(it.qty)}</td>
              <td className="uom">{it.uom}</td>
              <td className="r">{fmtMoney(it.price)}</td>
              <td className="r">{fmtMoney(it.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <table className="totals">
        <tbody>
          {m.totals.map(([label, v]) => (
            <tr key={label}>
              <td style={{ width: '64%', textAlign: 'right' }}>{label}</td>
              <td />
              <td style={{ width: '14%', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtMoney(v)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ fontSize: '8pt', marginTop: '0.5rem' }}>{m.countLine}</div>
      <div className="words">{m.words.map((l) => <div key={l}>{l}</div>)}</div>

      <div className="signer">
        <span>Виписав(ла):</span>
        <span className="line">{m.signerName}</span>
      </div>
    </div>
  );
}
